import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable, takeUntil } from 'rxjs';
import {
    AvatarBulkServer,
    AvatarsAvailable,
    AvatarServer
} from '../../core-data-model';
import { InitDataService } from '../../system-services';
import { AvatarApi } from './avatar-api.connnect';
import { GravatarAPIService } from './gravatar-api.connect';
import { Md5 } from './md5';

@Injectable({
    providedIn: 'root'
})
export class AvatarService implements OnDestroy {
    loggedInAvatarUrl$: Subject<string> = new Subject();
    avatarInitials$: Subject<string> = new Subject();

    private userEmail = '';
    private destroyed$ = new Subject<void>();

    constructor(
        private avatarApi: AvatarApi,
        private init: InitDataService,
        private gravatarApi: GravatarAPIService
    ) {
        this.userEmail = this.init.user.email;

        this.avatarApi
            .getAvatar(this.userEmail)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((avatar) => {
                if (avatar.avatarUrl) {
                    this.loggedInAvatarUrl$.next(avatar.avatarUrl);
                } else {
                    this.getDefaultAvatarInitials();
                }
            });
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    selectPreferredAvatar(currentAvatarUrl: string) {
        this.avatarApi
            .updateAvatar(this.userEmail, currentAvatarUrl)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((avatar) => {
                if (avatar.selectedAvatarUrl) {
                    this.loggedInAvatarUrl$.next(avatar.selectedAvatarUrl);
                } else {
                    this.getDefaultAvatarInitials();
                }
            });
    }

    getAvatar(userEmail: string): Observable<AvatarServer> {
        return this.avatarApi.getAvatar(userEmail);
    }

    getOwnAvailableAvatars(): Observable<AvatarsAvailable> {
        return this.avatarApi.getAvailableAvatars(this.userEmail);
    }

    getAvailableAvatars(userEmail: string): Observable<AvatarsAvailable> {
        return this.avatarApi.getAvailableAvatars(userEmail);
    }

    getAllCompanyAvatars(): Observable<AvatarBulkServer> {
        return this.avatarApi.getBulkAvatars();
    }

    checkForGravatar(email: string): Observable<boolean> {
        const hashedEmail = this.hashEmailForGravatar(email);
        return this.gravatarApi.checkGravatar(hashedEmail);
    }

    private hashEmailForGravatar(email: string): string {
        const cleanEmail = email.trim().toLowerCase();
        return Md5.hashStr(cleanEmail);
    }

    private getDefaultAvatarInitials() {
        const firstInitial =
            this.init.profile.firstName?.charAt(0)?.toUpperCase() ?? '';
        const lastInitial =
            this.init.profile.lastName?.charAt(0)?.toUpperCase() ?? '';

        this.avatarInitials$.next(`${firstInitial}${lastInitial}`.trim());
    }
}
